// Название объекта без названия и абзац-ссылка перед объектом - по тексту документа (как stoweb/autonames.py и
// autorefs.py при загрузке .docx), для быстрых исправлений живой проверки (lint.ts):
//   suggestName   - «Подставить «…»»: а) предложение со ссылкой на объект («На рисунке 3 показана схема…» -> «Схема…»,
//                   «Результаты измерений приведены в таблице 2» -> «Результаты измерений», «Показатель (рисунок 4):»),
//                   в) короткая строка над объектом («Схема подключения:»), г) головка таблицы («Входы и выходы»),
//                   д) заголовок раздела (общий «Ход работы» - нет; «Задание 3 Оценка…» - «Оценка…»), иначе тема работы;
//   refParts      - абзац-ссылка вокруг живой ссылки: «Схема стенда представлена на рисунке » … «.» / «Результат
//                   представлен на рисунке » … «.» (род - по первому слову названия); «Код программы (листинг » … «).».
import type { Node as PMNode } from '@tiptap/pm/model';
import type { DocIndex, IndexEntry } from '../../core/docIndex';

const PH_T = '\u0001';
const PH_R = '\u0002';
const PH_F = '\u0004';
const MATH = '⁢';
const OBJW = '(?:рисун[а-яё]*|рис\\.|таблиц[а-яё]*|табл\\.|листинг[а-яё]*)';
const PART = '(?:представлен|показан|приведен|приведён|изображен|изображён|отображен|отображён|продемонстрирован|'
  + 'проиллюстрирован|помещен|помещён|размещен|размещён|сведен|сведён|занесен|занесён|записан|вынесен|перечислен|описан|дан)';
const VERB = `(?:(?:был[аои]?|будут|будет)\\s+)?${PART}(?:а|о|ы)?`;
const ADV = '(?:ниже|далее|выше|также|здесь|затем|итак|кроме\\s+того)';
const S2 = new RegExp(`^(?:${ADV}[\\s,]+)?(?:на|в|во)\\s+${OBJW}\\s*${PH_T}\\s*,?\\s*(?:(?:${ADV}\\s+)?${VERB}\\s+|[–—-]\\s*)([\\s\\S]+)$`, 'i');
const S1 = new RegExp(`^([\\s\\S]+?)[\\s,]+(?:${ADV}\\s+)?${VERB}\\s+(?:${ADV}\\s+)?(?:на|в|во)\\s+${OBJW}\\s*${PH_T}(?:\\s+${ADV})?$`, 'i');
const S3 = new RegExp(`^([\\s\\S]+?)\\s*\\(\\s*(?:см\\.\\s*)?${OBJW}\\s*${PH_T}\\s*\\)$`, 'i');
const set = (s: string) => new Set(s.split(/\s+/));
const STOP_FIRST = set('это этот эта эти этой этом этого он она оно они его её ее их мы я вы ты нам нас нами вам вас '
  + 'который которая которое которые также далее затем ниже выше здесь там тогда так как что где если когда для при после '
  + 'перед в во на по с со из к ко от до и а но или же ещё еще уже вот все всё поэтому потому причём причем однако '
  + 'следовательно итак сначала потом теперь тут сам сама сами такой такая такое такие данный данная данное был была было '
  + 'были будет будут есть нет да можно нужно необходимо следует требуется видно');
const STOP_LAST = set('и а но или в во на по с со из к ко от до для при за над под о об про который которая которое '
  + 'которые что как где чтобы же ли бы');
const VERB_FIRST = /(?:ем|ём|им|или|али|ели|яли|ыли|шли|ите|йте|ете|ить|ать|ять|еть|уть|ыть|оть|ти|чь|ся|сь|ешь|ишь)$/;
const NOUN_OK = set('режим объём объем приём прием подъём подъем интерфейс сеть путь суть нить медь треть модели цели '
  + 'роли нули панели сети части пути ступени связи единицы');
const FINITE = set('имеет имеют выглядит выглядят зависит зависят содержит содержат показывает показывают представляет '
  + 'представляют отображает отображают изображает демонстрирует иллюстрирует видно можно нужно необходимо следует '
  + 'требуется получается получаем получили получим видим наблюдается происходит является являются есть был была было '
  + 'были будет будут стал стала стало стали сделаем сделали построим построили найдём найдем нашли определим определили '
  + 'вычислим вычислили составим составили рассмотрим рассмотрели проведём проведем провели соберём соберем собрали '
  + 'выполним выполнили рассчитаем рассчитали запишем записали введём введем ввели нажмём нажмем нажали откроем открыли '
  + 'создадим создали добавим добавили выберем выбрали подключим подключили');
const INTRO_BAD = set('следующий следующая следующее следующие следующим следующем следующей ниже выше таким образом '
  + 'например итак вид');
const LABEL = /^\s*(?:Цель|Задача|Задание|Тема|Вариант|Ход\s+работы|Порядок\s+выполнения|Вывод|Выводы|Ответ|Решение|Дано|Найти|Примечание|Пример|Рисунок|Рис\.|Таблица|Табл\.|Листинг|Продолжение|Окончание)(?![а-яё])/i;
const GENERIC_HEAD = /^(?:ход\s+(?:выполнения\s+)?работы|выполнение(?:\s+(?:лабораторной|практической|контрольной|расч[её]тно-графической))?\s+работы(?:\s*№?\s*\d+)?|(?:лабораторная|практическая|контрольная)\s+работа(?:\s*№?\s*\d+)?|(?:практическая|основная|экспериментальная|расч[её]тная|теоретическая)\s+часть|решение(?:\s+задач[иа]?)?|(?:задача|задание|упражнение|часть|этап|пункт)\s*№?\s*[\dIVXLC]+\.?|результаты(?:\s+работы)?|выполнение(?:\s+задани[яй])?|порядок\s+выполнения(?:\s+работы)?)$/i;
const TASK_PREFIX = /^(?:задание|задача|упражнение|часть|этап|пункт|шаг)\s*№?\s*[\dIVXLC]+(?:\.\d+)*[.:)]?\s+/i;
const UNIT_TAIL = /\s*(?:,\s*[^,]{1,14}|\[[^\]]{1,12}\]|\([^)]{1,12}\))$/;
const NUM_CELL = /^[\s\d.,:;+\-–−/()%×·*^°]*$/;
const WORD3 = /[А-Яа-яЁёA-Za-z]{3}/;

const words = (s: string) => s.match(/[А-Яа-яЁёA-Za-z][А-Яа-яЁёA-Za-z-]*/g) || [];

function verbish(w: string): boolean {
  const lw = w.toLowerCase().replace(/ё/g, 'е');
  if (FINITE.has(lw) || FINITE.has(lw.replace(/е/g, 'ё'))) return true;
  if (NOUN_OK.has(lw) || lw.length < 4) return false;
  return VERB_FIRST.test(lw) && !/(?:ость|есть|ние|тие|ция|ия)$/.test(lw);
}

function nameOk(s: string, label = false): boolean {
  s = s.trim();
  if (!s || s.length > 160 || /[\u0001-\u0004]/.test(s) || !WORD3.test(s)) return false;
  const ws = words(s);
  if (!ws.length || ws.length > (label ? 10 : 16)) return false;
  const w0 = ws[0] ?? '';
  const first = w0.toLowerCase();
  const last = (ws[ws.length - 1] ?? '').toLowerCase();
  if (STOP_FIRST.has(first) || verbish(w0) || STOP_LAST.has(last)) return false;
  // «Результаты … в табл. 2» - предложение со ссылкой; сказуемое у метки: «имеет вид», «предоставлены»
  if (/(?:рис|табл|листинг|рисун|таблиц|формул)[а-яё]*\.?\s*\(?\d/i.test(s)) return false;
  if (label && ws.slice(1).some((w) => { const l = w.toLowerCase(); return FINITE.has(l) || INTRO_BAD.has(l) || /^[а-яё]{4,}(?:ен|ён|ан|ян)[аоы]$/.test(l); })) return false;
  return true;
}

/** Без знаков по краям, без точки в конце (кроме сокращений), с прописной (кроме «pH»). */
export function tidyName(s: string): string {
  let t = s.replace(/[\u0001-\u0004]/g, '').replace(/\s+/g, ' ').replace(/^[\s,;:.–—-]+/, '').replace(/[\s,;:–—-]+$/, '');
  if (!/(?:\b(?:и\s+др|и\s+т\.\s?[дп]|т\.\s?е|см|рис|г|гг))\.$/.test(t)) t = t.replace(/(?<![.\s])\.$/, '');
  t = t.replace(/[\s,;:]+$/, '');
  const m = t.match(/^([«"(]?)([а-яёa-z])/);
  if (m && !/^[a-z][A-Z]/.test(t.slice(m[1].length, m[1].length + 2))) t = m[1] + m[2].toUpperCase() + t.slice(m[0].length);
  return t;
}

/** Плоский текст блока: ссылка на target - PH_T, другая ссылка - PH_R, формула - U+2062, прочее - PH_F; skipAuto -
 *  без дописанного сайтом (метка auto, xref.auto). */
function flat(n: PMNode, target: string | null, skipAuto = false): string {
  let s = '';
  n.forEach((c) => {
    const auto = c.isText ? c.marks.some((m) => m.type.name === 'auto') : c.type.name === 'xref' && !!c.attrs.auto;
    if (skipAuto && auto) return;
    if (c.isText) s += c.text || '';
    else if (c.type.name === 'xref') s += target && c.attrs.target === target ? PH_T : PH_R;
    else if (c.type.name === 'mathInline') s += MATH;
    else if (c.type.name === 'hardBreak') s += ' ';
    else s += PH_F;
  });
  return s;
}

function sentence(F: string, k: number): [number, number] {
  let st = 0;
  const re = /(?:(?<=[.!?…])\s+(?=[А-ЯЁA-Z«"(])|(?<=;)\s+)/g;
  const head = F.slice(0, k);
  let m: RegExpExecArray | null;
  while ((m = re.exec(head))) { st = m.index + m[0].length; if (!m[0].length) re.lastIndex++; }
  const e = F.slice(k).search(/[.!?…;](?=\s|$)/);
  return [st, e >= 0 ? k + e : F.length];
}

const isAutoPara = (n: PMNode) => {
  let txt = 0; let auto = 0;
  n.forEach((c) => { if (c.isText && (c.text || '').trim()) { txt++; if (c.marks.some((m) => m.type.name === 'auto')) auto++; } });
  return txt > 0 && txt === auto;
};

// id объекта -> абзац с первой ссылкой на него (кэш на документ: живая проверка зовёт suggestName для каждого
// объекта без названия)
const refCache = new WeakMap<PMNode, Map<string, PMNode>>();
function firstRefParas(doc: PMNode): Map<string, PMNode> {
  let m = refCache.get(doc);
  if (m) return m;
  const out = new Map<string, PMNode>();
  doc.descendants((n) => {
    if (['figure', 'stoTable', 'listing'].includes(n.type.name)) return false;
    if (n.type.name === 'paragraph') {
      if (!isAutoPara(n)) n.forEach((c) => { if (c.type.name === 'xref' && c.attrs.target && !out.has(c.attrs.target)) out.set(c.attrs.target, n); });
      return false;
    }
    return true;
  });
  m = out;
  refCache.set(doc, m);
  return m;
}

/** а) Из предложения с первой ссылкой на объект (абзац основного текста, не дописанный сайтом). */
function fromReference(doc: PMNode, id: string): string | null {
  const para = firstRefParas(doc).get(id);
  if (!para) return null;
  const F = flat(para, id);
  const k = F.indexOf(PH_T);
  if (k < 0 || F.indexOf(PH_T, k + 1) >= 0) return null;
  const [st, en] = sentence(F, k);
  const sent = F.slice(st, en).replace(/[\s:;.!?…]*$/, '');
  for (const [rx, label] of [[S2, false], [S1, false], [S3, true]] as [RegExp, boolean][]) {
    const m = sent.match(rx);
    if (!m) continue;
    let nm = m[1];
    if (rx === S2 && words(nm).length > 12 && nm.includes(',')) nm = nm.slice(0, nm.indexOf(','));
    if (nm.includes(MATH) || !nameOk(nm, label)) return null;
    return tidyName(nm);
  }
  return null;
}

/** Блок перед объектом на том же уровне (пустые абзацы пропускаются). */
export function prevBlock(doc: PMNode, pos: number): { node: PMNode; pos: number } | null {
  const $p = doc.resolve(pos);
  const parent = $p.parent;
  let idx = $p.index();
  let off = pos;
  while (idx > 0) {
    const n = parent.child(idx - 1);
    off -= n.nodeSize;
    idx--;
    if (n.type.name === 'paragraph' && !n.textContent.trim() && !n.childCount) continue;
    return { node: n, pos: off };
  }
  return null;
}

/** в) Короткая строка над объектом: «Схема подключения:», «Без регулятора». */
function fromIntro(doc: PMNode, pos: number): string | null {
  const p = prevBlock(doc, pos);
  if (!p || p.node.type.name !== 'paragraph' || isAutoPara(p.node)) return null;
  const t = flat(p.node, null, true).trim().replace(/[\s(]+$/, '');
  // «Иванов Иван Иванович», «Иванов И. И.» - строка с ФИО, не название
  if (!t || LABEL.test(t) || /^[А-ЯЁ][а-яё]+(?:-[А-ЯЁ][а-яё]+)?\s+(?:[А-ЯЁ][а-яё]+\s+[А-ЯЁ][а-яё]+(?:вич|вна|ична|чна|ич|оглы|кызы)|[А-ЯЁ]\.\s*[А-ЯЁ]\.)$/.test(t)) return null;
  const colon = t.endsWith(':');
  if (!colon && /[.!?;…]$/.test(t)) return null;
  const body = t.replace(/:$/, '').trim();
  if (words(body).length > (colon ? 10 : 8) || !/^[«"(]?[А-Яа-яЁё]/.test(body) || body.includes(MATH) || !nameOk(body, true)) return null;
  if (/[.!?;](?:\s|$)/.test(body)) return null;
  return tidyName(body);
}

/** г) Головка таблицы из 2–4 наименований: «Входы и выходы». */
function fromHeader(tbl: PMNode): string | null {
  let table: PMNode | null = null;
  tbl.forEach((c) => { if (c.type.name === 'table') table = c; });
  if (!table || !(table as PMNode).childCount) return null;
  const T = table as PMNode;
  let heads: string[] = [];
  T.child(0).forEach((c) => heads.push(c.textContent.trim()));
  if (heads.length > 2 && heads[0] && heads.slice(1).every((h) => !h || NUM_CELL.test(h))) {
    heads = [];
    T.forEach((r) => { heads.push(r.childCount ? r.child(0).textContent.trim() : ''); });
  }
  const items: string[] = [];
  for (let h of heads) {
    h = h.replace(/\s+/g, ' ').trim();
    if (!h || NUM_CELL.test(h) || /^(?:№|n|no)\s*(?:п\/?п|опыта|измерения)?\.?$/i.test(h)) continue;
    h = h.replace(UNIT_TAIL, '').trim();
    const ws = words(h);
    if (!ws.length || ws.length > 3 || !/^[А-Яа-яЁё]{3}/.test(h)) return null;
    items.push(h);
  }
  if (items.length < 2 || items.length > 4) return null;
  const xs = [items[0], ...items.slice(1).map((x) => (/^[А-ЯЁ]{2}/.test(x) ? x : x[0].toLowerCase() + x.slice(1)))];
  const name = xs.slice(0, -1).join(', ') + ' и ' + xs[xs.length - 1];
  if (name.length > 80 || new Set(xs.map((x) => x.toLowerCase())).size < xs.length) return null;
  return tidyName(name);
}

/** Заголовок раздела над объектом (в приложении - его заголовок) без номера задания; общий («Ход работы») - null. */
function headingOf(ix: DocIndex, e: IndexEntry): string | null {
  let head: string | null = null;
  for (const x of ix.entries) {
    if (x.pos >= e.pos) break;
    if (x.appendix !== e.appendix) continue;
    if (x.type === 'heading') head = x.text.trim();
    else if (x.type === 'structHeading') head = null;
    else if (x.type === 'appendix' && e.appendix === x.id) {
      let t = '';
      x.node.forEach((c) => { if (c.type.name === 'appendixTitle') t = c.textContent.trim(); });
      head = t || null;
    }
  }
  if (!head) return null;
  const m = head.match(TASK_PREFIX);
  if (m && words(head.slice(m[0].length)).length >= 2) head = head.slice(m[0].length);
  return !GENERIC_HEAD.test(head.replace(/[.:]+$/, '')) && WORD3.test(head) ? head : null;
}

/** д) Заголовок раздела, иначе тема работы (без шифра в конце: «…маятника. ФМ - 16»). */
function fromHeading(ix: DocIndex, e: IndexEntry): string | null {
  const head = headingOf(ix, e);
  if (head) return tidyName(head);
  const topic = !e.appendix ? String(ix.meta?.title || '').replace(/\s+/g, ' ').trim()
    .replace(/\s*[.,;]\s*(?:[А-ЯЁA-Z]{1,5}\s*[-–—]?\s*\d+[А-Яа-яЁё]?|вариант\s*№?\s*\d+|в-?\s*\d+)\s*$/i, '').replace(/\.$/, '') : '';
  return topic && WORD3.test(topic) ? tidyName(topic) : null;
}

const GENERIC_NAME = set('результаты результат данные значения показания измерения расчёты расчеты параметры характеристики итоги');
const GEN_RULES: [RegExp, string][] = [[/([нтв]и)е$/, '$1я'], [/([кг])а$/, '$1и'], [/([цсз]и)я$/, '$1и'], [/ость$/, 'ости'],
  [/(ч[её]т)$/, '$1а'], [/(из|ез|ент|ыт|аж)$/, '$1а']];

/** «Результаты» + «Исследование работы датчика» -> «Результаты исследования работы датчика» (как autonames.with_heading). */
function withHeading(name: string, ix: DocIndex, e: IndexEntry): string {
  if (!GENERIC_NAME.has(name.trim().toLowerCase())) return name;
  const head = headingOf(ix, e);
  const m = head?.match(/^[А-ЯЁа-яё]+/);
  if (!head || !m || (m[0].length > 1 && m[0] === m[0].toUpperCase())) return name;
  const g = genitive(m[0].toLowerCase());
  if (!g) return name;
  let rest = head.slice(m[0].length);
  // «Снятие и построение характеристики» -> «снятия и построения характеристики»
  const m2 = rest.match(/^(\s+и\s+)([а-яё]+)/);
  const g2 = m2 ? genitive(m2[2] ?? '') : null;
  if (m2 && g2) rest = m2[1] + g2 + rest.slice(m2[0].length);
  const out = tidyName(`${name} ${g}${rest}`);
  return out.length <= 150 ? out : name;
}

function genitive(w: string): string | null {
  for (const [rx, rep] of GEN_RULES) if (rx.test(w)) return w.replace(rx, rep);
  return null;
}

/** Название для объекта без названия (или null). */
export function suggestName(ix: DocIndex, e: IndexEntry): string | null {
  const r = fromReference(ix.doc, e.id) || fromIntro(ix.doc, e.pos);
  const n = (r ? withHeading(r, ix, e) : null) || (e.type === 'stoTable' ? fromHeader(e.node) : null) || fromHeading(ix, e);
  return n && n.trim() ? n : null;
}

/** Род первого слова названия: 0 - м., 1 - ж., 2 - ср., 3 - мн.; null - неясно (как autorefs.gender). */
export function gender(name: string): number | null {
  const ws = words(name);
  if (!ws.length) return null;
  const w0 = (ws[0] ?? '').split('-').pop() || '';
  if (!/^[А-Яа-яЁё]+$/.test(w0) || (w0.length > 1 && w0 === w0.toUpperCase())) return null;
  const w = w0.toLowerCase();
  if (w.length < 3) return null;
  const rules: [RegExp, number | null][] = [[/(?:ая|яя)$/, 1], [/(?:ый|ий|ой)$/, 0], [/(?:ое|ее)$/, 2],
    [/(?:ые|ские|цкие|кие|гие|хие|шие|щие)$/, 3], [/(?<!нали)чие$/, 3], [/мя$/, 2], [/(?:ения|ания|тия|вия|ства)$/, 3],
    [/(?:ие|ье|о|е)$/, 2], [/(?:а|я)$/, 1], [/(?:ость|есть)$/, 1], [/тель$/, 0], [/ь$/, null], [/(?:ы|и)$/, 3],
    [/[бвгджзйклмнпрстфхцчшщ]$/, 0]];
  for (const [rx, g] of rules) if (rx.test(w)) return g;
  return null;
}

const PARA_VERB: Record<string, [string[], string, string]> = {
  figure: [['представлен', 'представлена', 'представлено', 'представлены'], 'на рисунке ', 'Результат представлен на рисунке '],
  stoTable: [['приведён', 'приведена', 'приведено', 'приведены'], 'в таблице ', 'Данные приведены в таблице '],
};

/** Абзац-ссылка перед объектом: [начало, конец] вокруг живой ссылки - по названию, если ясен род («Схема стенда
 *  представлена на рисунке » … «.»), иначе общая фраза; листинг - в скобках в конце предложения (6.1.5): «Код
 *  программы (листинг » … «).». */
export function refParts(type: string, caption: string): [string, string] {
  const t = tidyName(caption || '');
  // «Таблица состояний …» - не «Таблица состояний приведена в таблице 1»
  const ok = !!t && t.length <= 100 && nameOk(t) && !/^(?:рисун|таблиц|листинг)/i.test(t);
  if (type === 'listing') return [`${ok ? t : 'Текст программы'} (листинг `, ').'];
  const [verbs, where, generic] = PARA_VERB[type] || PARA_VERB.figure!;
  const g = ok ? gender(t) : null;
  return [g !== null ? `${t} ${verbs[g]} ${where}` : generic, '.'];
}
